import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import aa from "search-insights";
import SwiperCore, { FreeMode } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import IntersectionWrapper from "~/components/common/intersection-wrapper";
import SkeletonProductTile from "~/components/common/skeletons/skeleton-product-tile";
import { useConfiguration } from "~/contexts/configuration";
import { getUserTokens } from "~/services/algolia-utils";
import Algolia from "~/types/algolia";
import Constants from "~/utils/constants";
import TrackingUtils from "~/utils/tracking-utils";

import { CarouselArrows, updateArrowStatus } from "../carousel-utils/carousel-utils";
import styles from "./product-carousel-swiper.module.scss";

const ProductTile = dynamic(() => import("../../../components/products/product-tile"), {
  loading: () => <SkeletonProductTile />,
});
const ProductTileAlgolia = dynamic(() => import("../../../components/products/product-tile-algolia"), {
  loading: () => <SkeletonProductTile />,
});

type Props = {
  products?: any[];
  enableLoop?: boolean;
  listScope?: string;
  isAlgolia?: boolean;
};

export default function ProductCarouselSwiper({ products, enableLoop, listScope, isAlgolia = false }: Props) {
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore>();
  const [arrowsStatus, setArrowsStatus] = useState({ arrowL: true, arrowR: true });
  const [isFirstRender, setIsFirstRender] = useState(true);
  const { algoliaIndexId } = useConfiguration();

  const MAX_SLIDES_DESKTOP = 5;
  const isInfiniteScroll = enableLoop && products && products.length >= MAX_SLIDES_DESKTOP * 2;

  const [visibleSlides, setVisibleSlides] = useState(Constants.MAX_PRODUCTS_LOADED_IN_CAROUSEL);
  const [countImage, setCountImage] = useState(0);

  useEffect(() => {
    if (swiperInstance) {
      setArrowsStatus(updateArrowStatus(swiperInstance, isInfiniteScroll));
    }
  }, [isInfiniteScroll, swiperInstance]);

  useEffect(() => {
    if (countImage >= visibleSlides) {
      setVisibleSlides((prev) => prev + Constants.MAX_PRODUCTS_LOADED_IN_CAROUSEL);
    }
  }, [countImage, visibleSlides]);

  const imageLoaded = () => {
    setCountImage((prev) => prev + 1);
  };

  const clickEvent = (algoliaProduct: Algolia.Product) => {
    if (!isAlgolia) {
      return;
    }

    const { currentUserToken, currentAuthenticatedUserToken } = getUserTokens();
    const objectIDs = [algoliaProduct.objectID];

    if ("__queryID" in algoliaProduct) {
      aa("clickedObjectIDsAfterSearch", {
        userToken: currentUserToken,
        authenticatedUserToken: currentAuthenticatedUserToken,
        index: algoliaIndexId,
        eventName: "Hit Clicked",
        queryID: algoliaProduct.__queryID,
        objectIDs,
        positions: [algoliaProduct.__position],
      });

      // save it in localstorage for cross-tabs navigation and set it as default on session
      localStorage.setItem("algolia_queryid", algoliaProduct.__queryID as string);
      sessionStorage.setItem("algolia_queryid", algoliaProduct.__queryID as string);
    } else {
      aa("clickedObjectIDs", {
        userToken: currentUserToken,
        authenticatedUserToken: currentAuthenticatedUserToken,
        index: algoliaIndexId,
        eventName: "Hit Clicked",
        objectIDs,
      });
    }
  };

  return (
    <IntersectionWrapper
      once={true}
      onIntersection={(inView: boolean) => {
        if (products && inView && listScope) {
          TrackingUtils.track(
            TrackingUtils.mapEcommerceEvent(
              "view_item_list",
              products.map((e, i) =>
                TrackingUtils.mapEcomItem(e, e.children?.[0], { index: i, item_list_name: listScope })
              ),
              { user_action: "impression" }
            )
          );
        }
      }}
    >
      <Swiper
        modules={[FreeMode]}
        freeMode={{ enabled: true, sticky: false }}
        onSwiper={setSwiperInstance}
        onTransitionEnd={(currentSwiper) => setArrowsStatus(updateArrowStatus(currentSwiper, isInfiniteScroll))}
        onTouchEnd={() => setIsFirstRender(false)}
        centeredSlidesBounds
        grabCursor
        speed={Constants.SWIPER_TRANSITION_DURATION}
        slidesPerView={1.6}
        centeredSlides={products && products.length > 2 && (isFirstRender || !isInfiniteScroll)}
        className={styles.swiper}
        initialSlide={1}
        threshold={10}
        loop={isInfiniteScroll}
        spaceBetween={12}
        breakpoints={{
          640: {
            slidesPerView: 2.8,
            centeredSlides: products && products.length > 3 && (isFirstRender || !isInfiniteScroll),
            freeMode: { enabled: true, sticky: false },
          },
          900: {
            slidesPerView: 4,
            centeredSlides: products && products.length > 4 && (isFirstRender || !isInfiniteScroll),
            freeMode: { enabled: true, sticky: true },
          },
          1280: {
            slidesPerView: MAX_SLIDES_DESKTOP,
            centeredSlides: products && products.length > MAX_SLIDES_DESKTOP && (isFirstRender || !isInfiniteScroll),
            freeMode: { enabled: true, sticky: true },
          },
        }}
      >
        {products
          ? products.map((product, i) => (
              <SwiperSlide key={i} className={styles.slide}>
                {i < visibleSlides ? (
                  !isAlgolia ? (
                    <ProductTile
                      product={product}
                      idxInList={i}
                      listScope={listScope}
                      isInCarousel
                      imageLoaded={
                        products.length > Constants.MAX_PRODUCTS_LOADED_IN_CAROUSEL ? imageLoaded : undefined
                      }
                    />
                  ) : (
                    <ProductTileAlgolia
                      product={product}
                      listScope={listScope}
                      isInCarousel
                      imageLoaded={
                        products.length > Constants.MAX_PRODUCTS_LOADED_IN_CAROUSEL ? imageLoaded : undefined
                      }
                      algolia={isAlgolia ? { product, events: { clickEvent } } : undefined}
                    />
                  )
                ) : (
                  <SkeletonProductTile />
                )}
              </SwiperSlide>
            ))
          : Array.from({ length: Constants.MAX_PRODUCTS_LOADED_IN_CAROUSEL }).map((_, i) => (
              <SwiperSlide key={i} className={styles.initialSlide}>
                <SkeletonProductTile />
              </SwiperSlide>
            ))}
      </Swiper>
      {swiperInstance && <CarouselArrows swiperInstance={swiperInstance} arrowsStatus={arrowsStatus} />}
    </IntersectionWrapper>
  );
}
